import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Music4, AlertCircle, ArrowLeft, Sparkles } from "lucide-react";
import { api, apiError, setToken, APP_NAME } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asset = (u, fb) => { if (!u) return fb; if (u.startsWith("/api/")) return `${BACKEND}${u}`; return u; };
const HERO = "https://images.unsplash.com/photo-1484876065684-b683cf17d276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxtdXNpY2lhbiUyMHBsYXlpbmclMjBsaXZlfGVufDB8fHx8MTc4NjEyNzk3NXww&ixlib=rb-4.1.0&q=85";
const DEF_TITLE = "A comunidade dos músicos freelancers.";
const DEF_SUB = "Crie oportunidades, candidate-se a gigs, gerencie sua agenda e evite conflitos de horário — tudo em um só lugar. Sem grupos de WhatsApp.";
const setFavicon = (href) => { if (!href) return; let l = document.querySelector("link[rel~='icon']"); if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); } l.href = href; };

export default function Auth({ mode, preview }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ email: "", password: "", stage_name: "", full_name: "" });
  const [errors, setErrors] = useState({});
  const refs = useRef({});
  const [verifyMsg, setVerifyMsg] = useState("Verificando...");
  const [cfg, setCfg] = useState(null);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    api.get(preview ? "/admin/landing" : "/public/landing").then((r) => { setCfg(r.data); const fav = asset(r.data?.favicon) || asset(r.data?.img_logo); if (fav) setFavicon(fav); }).catch(() => {});
    api.get("/branding").then((r) => setBranding(r.data)).catch(() => {});
    const prev = document.documentElement.getAttribute("data-theme");
    document.documentElement.setAttribute("data-theme", "dark");
    return () => { if (prev) document.documentElement.setAttribute("data-theme", prev); };
  }, []);

  useEffect(() => {
    if (mode === "verify") {
      const token = params.get("token");
      api.post("/auth/verify-email", { token })
        .then(() => setVerifyMsg("E-mail confirmado com sucesso! Você já pode usar a plataforma."))
        .catch((e) => setVerifyMsg(apiError(e.response?.data?.detail)));
    }
  }, [mode]); // eslint-disable-line

  const set = (k) => (e) => {
    const v = e.target.value;
    setF((p) => ({ ...p, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const validate = () => {
    const er = {};
    if (mode === "register" && !f.stage_name.trim()) er.stage_name = "Nome artístico obrigatório — verifique este campo.";
    if (mode !== "reset") {
      if (!f.email.trim()) er.email = "E-mail obrigatório — verifique este campo.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) er.email = "E-mail inválido — verifique o formato (ex: voce@email.com).";
    }
    if (mode !== "forgot") {
      if (!f.password) er.password = "Senha obrigatória — verifique este campo.";
      else if (mode === "register" && f.password.length < 6) er.password = "A senha precisa ter ao menos 6 caracteres.";
    }
    return er;
  };

  const errCls = (name) => (errors[name] ? "border-destructive focus-visible:ring-destructive" : "border-white/10");
  const aria = (name) => ({ "aria-invalid": !!errors[name], "aria-describedby": errors[name] ? `err-${name}` : undefined, ref: (el) => { refs.current[name] = el; } });
  const FieldError = ({ name }) => (errors[name] ? (
    <p id={`err-${name}`} data-testid={`err-${name}`} role="alert" className="mt-1 flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{errors[name]}</p>
  ) : null);

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      const first = Object.keys(er)[0];
      const el = refs.current[first];
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); }
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
        setToken(data.token); setUser(data); navigate("/");
      } else if (mode === "register") {
        const ref = params.get("ref");
        let utm = null;
        try { utm = JSON.parse(localStorage.getItem("mc_utm") || "null"); } catch (e) { utm = null; }
        const payload = { ...f };
        if (ref) payload.ref = ref;
        if (utm) payload.utm = utm;
        const { data } = await api.post("/auth/register", payload);
        setToken(data.token); setUser(data);
        try { window.dataLayer && window.dataLayer.push({ event: "signup_complete" }); } catch (e) {}
        toast.success("Conta criada! Enviamos um e-mail de confirmação.");
        navigate("/");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email });
        toast.success("Se o e-mail existir, enviamos um link de recuperação.");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token"), password: f.password });
        toast.success("Senha redefinida! Faça login.");
        navigate("/login");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const titles = {
    login: "Entrar", register: "Criar conta", forgot: "Recuperar senha",
    reset: "Nova senha", verify: "Confirmação de e-mail",
  };

  const logo = asset(cfg?.img_logo) || asset(branding?.logo_main) || "/michamou-icon.png";
  const logomarca = asset(cfg?.img_logomarca);
  const authImage = asset(cfg?.auth_image, HERO);
  const authTitle = cfg?.auth_title || DEF_TITLE;
  const authSubtitle = cfg?.auth_subtitle || DEF_SUB;

  const Brand = ({ size = "text-2xl", h = "h-10" }) => (
    logomarca
      ? <img src={logomarca} alt={APP_NAME} className={`${h} w-auto object-contain`} />
      : <div className="flex items-center gap-2"><img src={logo} alt={APP_NAME} className={`${h} w-auto max-w-[44px] rounded-lg object-cover`} /><span className={`font-head font-extrabold ${size} tracking-tight`}>{APP_NAME}</span></div>
  );

  const inputCls = (name) => `mt-1.5 bg-[#121217] rounded-xl h-11 text-white placeholder:text-zinc-600 focus-visible:ring-accent ${errCls(name)}`;
  const ctaPrimary = "w-full inline-flex items-center justify-center gap-2 h-12 font-semibold rounded-full bg-gradient-to-r from-[#FF4D00] to-accent text-white shadow-[0_0_28px_rgba(255,79,0,0.35)] hover:shadow-[0_0_40px_rgba(255,79,0,0.5)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0";

  return (
    <div className="min-h-screen flex bg-[#09090B] text-white antialiased selection:bg-accent/30" data-testid="auth-page">
      {preview && <div data-testid="preview-banner" className="fixed top-2 left-1/2 -translate-x-1/2 z-[80] text-xs px-3 py-1 rounded-full bg-amber-500 text-black font-bold shadow-lg">Pré-visualização (rascunho)</div>}
      {/* PAINEL LATERAL */}
      <div className="hidden lg:block w-1/2 relative overflow-hidden">
        <img src={authImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/80 to-[#09090B]/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(255,79,0,0.35),transparent_45%),radial-gradient(circle_at_90%_100%,rgba(255,160,0,0.18),transparent_40%)]" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" aria-hidden="true" />
        <div className="absolute top-10 left-12"><Brand /></div>
        <div className="absolute bottom-12 left-12 right-12">
          <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-accent/15 border border-accent/30 text-accent mb-4"><Sparkles className="h-3.5 w-3.5" />Música conecta pessoas</span>
          <h1 className="font-head text-4xl font-bold tracking-tight leading-tight max-w-md">{authTitle}</h1>
          <p className="mt-4 text-zinc-300 max-w-md">{authSubtitle}</p>
        </div>
      </div>

      {/* FORMULÁRIO */}
      <div className="flex-1 flex items-center justify-center p-6 relative">
        <div className="lg:hidden absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(255,79,0,0.18),transparent_50%)]" aria-hidden="true" />
        <div className="w-full max-w-sm relative fade-up">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-6" data-testid="back-to-site"><ArrowLeft className="h-4 w-4" />Voltar ao site</Link>
          <div className="lg:hidden mb-8"><Brand size="text-xl" h="h-9" /></div>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mb-1.5">{titles[mode]}</h2>
          <p className="text-zinc-400 text-sm mb-8">
            {mode === "login" && "Acesse sua conta para continuar."}
            {mode === "register" && "Junte-se à comunidade de músicos."}
            {mode === "forgot" && "Enviaremos um link para redefinir sua senha."}
            {mode === "reset" && "Escolha uma nova senha para sua conta."}
            {mode === "verify" && verifyMsg}
          </p>

          {mode === "verify" ? (
            <Button data-testid="go-login-btn" onClick={() => navigate("/login")} className={ctaPrimary}>Ir para o login</Button>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4">
              {mode === "register" && (
                <>
                  <div><Label className="text-zinc-300">Nome artístico <span className="text-destructive">*</span></Label>
                    <Input data-testid="stage-name-input" {...aria("stage_name")} value={f.stage_name} onChange={set("stage_name")} placeholder="Ex: Johnny Blues" className={inputCls("stage_name")} />
                    <FieldError name="stage_name" /></div>
                  <div><Label className="text-zinc-300">Nome completo</Label>
                    <Input data-testid="full-name-input" value={f.full_name} onChange={set("full_name")} placeholder="Seu nome" className="mt-1.5 bg-[#121217] border-white/10 rounded-xl h-11 text-white placeholder:text-zinc-600 focus-visible:ring-accent" /></div>
                </>
              )}
              {mode !== "reset" && (
                <div><Label className="text-zinc-300">E-mail <span className="text-destructive">*</span></Label>
                  <Input data-testid="email-input" type="email" {...aria("email")} value={f.email} onChange={set("email")} placeholder="voce@email.com" className={inputCls("email")} />
                  <FieldError name="email" /></div>
              )}
              {mode !== "forgot" && (
                <div><Label className="text-zinc-300">Senha <span className="text-destructive">*</span></Label>
                  <Input data-testid="password-input" type="password" {...aria("password")} value={f.password} onChange={set("password")} placeholder="••••••••" className={inputCls("password")} />
                  <FieldError name="password" /></div>
              )}
              {mode === "login" && (
                <div className="text-right"><Link to="/forgot-password" className="text-xs text-accent hover:underline">Esqueci minha senha</Link></div>
              )}
              <Button data-testid="submit-btn" disabled={loading} type="submit" className={ctaPrimary}>
                <Music4 className="h-5 w-5" />{loading ? "Aguarde..." : titles[mode]}
              </Button>
            </form>
          )}

          {(mode === "login" || mode === "register") && (
            <p className="mt-6 text-center text-sm text-zinc-500">
              {mode === "login" ? "Não tem conta? " : "Já tem conta? "}
              <Link data-testid="switch-auth-link" to={mode === "login" ? "/register" : "/login"} className="text-accent font-semibold hover:underline">
                {mode === "login" ? "Cadastre-se" : "Entrar"}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
